import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  IconName,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@flatfile/baseline'
import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActiveEnvironment, useHasEnvironmentsAccess } from '../../hooks/useEnvironments'
import { useIdentity } from '../../hooks/useIdentity'
import CreateAppPath from '../Apps/CreateAppModalPath'
import { renderMappedIcon } from '../Shared/IconMapper'

interface EnvironmentFilters {
  type: string[]
}

interface CommonAppProperties {
  id?: string
  name?: string
  icon?: string
  activatedAt?: string
  action?: string
  environmentFilters?: EnvironmentFilters
  label?: string
}

type PlatformApp = CommonAppProperties & {
  label: string
  action?: string
}

type CustomApp = CommonAppProperties & {
  // Custom app-specific properties here
}

type DemoApp = CommonAppProperties & {
  // Demo app-specific properties here
}

// Props type definition for the component
interface AppSwitcherProps {
  platformApps: PlatformApp[]
  customApps: CustomApp[]
  demoApps: DemoApp[]
  name: string
  icon: IconName
}

export const AppSwitcher: React.FC<AppSwitcherProps> = ({ platformApps, customApps, demoApps, name, icon }) => {
  const nav = useNavigate()
  const [openCreateAppModal, setOpenCreateAppModal] = useState<(() => void) | null>(null)

  const handleExternalTrigger = useCallback((triggerFn: () => void) => {
    setOpenCreateAppModal(() => triggerFn)
  }, [])

  const { environment } = useActiveEnvironment()
  const { identity, hasPermission } = useIdentity()
  const hasEnvAccess = useHasEnvironmentsAccess()

  // Existing filter function based on 'action' property
  const filterAppsByAction = (app: CommonAppProperties) => !app.action || app.action === 'Finish Setup'

  // New filter function based on environment
  const filterAppsByEnvironment = (app: PlatformApp | CustomApp | DemoApp) => {
    //TODO: add logic to filter out apps that have no spaces
    if (!hasEnvAccess) return true // If user is a space only user, show all
    const filterType = environment.isProd ? 'PROD' : 'DEV'
    return app.environmentFilters?.type?.includes(filterType) ?? false
  }

  const filteredPlatformApps = platformApps.filter(filterAppsByAction).filter(filterAppsByEnvironment)
  const filteredDemoApps = demoApps.filter(filterAppsByEnvironment)
  const filteredCustomApps = customApps.filter(filterAppsByEnvironment)

  return (
    <>
      <div className='flex'>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              className='flex items-center gap-2 hover:bg-interactive-state-selected-enabled -ml-2 px-2'
            >
              {renderMappedIcon({ iconName: icon, className: 'text-primary' })}
              <div className='flex items-center gap-2'>
                <Text variant='headline' emphasized className='text-base text-primary'>
                  {name.length > 13 ? `${name.substring(0, 13)}...` : name}
                </Text>
                <Icon name='chevron-bottom' size={12} color='primary' />
              </div>
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent className='ml-2 rounded-md overflow-hidden p-0'>
            <div className='max-h-[230px] min-w-[200px]  overflow-y-auto rounded-md p-1'>
              {filteredPlatformApps.length > 0 && (
                <>
                  <DropdownMenuLabel className='text-sm'>Core Apps</DropdownMenuLabel>

                  {filteredPlatformApps.map((link) => (
                    <DropdownMenuItem
                      key={link.label}
                      className='flex items-center gap-2'
                      onClick={() => {
                        const actionPath = link.action === 'Finish Setup' ? 'start/' : 'list/recent/'
                        nav(`/apps/${link.label.toLowerCase()}/${actionPath}`)
                      }}
                    >
                      <span className='flex-shrink-0 text-primary'>
                        {renderMappedIcon({ iconName: link.icon as IconName, size: 18, className: 'text-primary' })}
                      </span>
                      <Text className='flex-grow truncate max-w-[180px] text-sm text-primary'>{link.label}</Text>
                      {link.action && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              size='icon-sm'
                              variant='ghost'
                              className='text-gray-400 flex-shrink-0 hover:bg-interactive-state-selected-enabled'
                              icon='circle-info'
                              iconProps={{ size: 16 }}
                            ></Button>
                          </TooltipTrigger>
                          <TooltipContent className='flex items-center px-2 py-1 z-[51]'>
                            <Text variant='body' className='text-sm'>
                              {link.action}
                            </Text>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </DropdownMenuItem>
                  ))}
                </>
              )}

              {filteredCustomApps.length > 0 && filteredPlatformApps.length > 0 && <DropdownMenuSeparator />}
              {filteredCustomApps.length > 0 && filteredPlatformApps.length > 0 && (
                <DropdownMenuLabel className='text-sm'>Custom Apps</DropdownMenuLabel>
              )}

              {filteredCustomApps.map((app) => (
                <DropdownMenuItem
                  key={app.id}
                  className='flex items-center gap-2'
                  onClick={() => {
                    nav(app.activatedAt ? `/apps/custom/${app.id}/list/` : `/apps/custom/${app.id}/start/`)
                  }}
                >
                  <span className='flex-shrink-0 text-primary'>
                    {renderMappedIcon({ iconName: app.icon as IconName, size: 18 })}
                  </span>
                  <Text className='flex-grow truncate max-w-[180px] text-sm text-primary'>{app.name}</Text>
                  {!app.activatedAt && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size='icon-sm'
                          variant='ghost'
                          className='text-gray-400 flex-shrink-0 hover:bg-interactive-state-selected-enabled'
                          icon='status-warning-circle'
                          iconProps={{ size: 16, color: 'primary' }}
                        ></Button>
                      </TooltipTrigger>
                      <TooltipContent className='flex items-center px-2 py-1'>
                        <Text variant='body' className='text-sm'>
                          Finish Setup
                        </Text>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </DropdownMenuItem>
              ))}

              {filteredDemoApps.length > 0 && (filteredPlatformApps.length > 0 || filteredCustomApps.length > 0) && (
                <DropdownMenuSeparator />
              )}
              {filteredDemoApps.length > 0 && <DropdownMenuLabel className='text-sm'>Demo Apps</DropdownMenuLabel>}

              {filteredDemoApps.map((app) => (
                <DropdownMenuItem
                  key={app.id}
                  className='flex items-center gap-2'
                  onClick={() => {
                    nav(app.activatedAt ? `/apps/custom/${app.id}/list/` : `/apps/custom/${app.id}/start/`)
                  }}
                >
                  <span className='flex-shrink-0 text-primary'>
                    {renderMappedIcon({ iconName: app.icon as IconName, size: 18 })}
                  </span>
                  <Text className='flex-grow truncate max-w-[180px] text-sm text-primary'>{app.name}</Text>
                  {!app.activatedAt && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size='icon-sm'
                          variant='ghost'
                          className='text-gray-400 flex-shrink-0 hover:bg-interactive-state-selected-enabled'
                          icon='status-warning-circle'
                          iconProps={{ size: 16, color: 'primary' }}
                        ></Button>
                      </TooltipTrigger>
                      <TooltipContent className='flex items-center px-2 py-1'>
                        <Text variant='body' className='text-sm'>
                          Finish Setup
                        </Text>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </DropdownMenuItem>
              ))}

              {hasPermission({ resource: identity.account, permissionKey: 'app:create' }) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    key='add-app'
                    className='flex items-center gap-2'
                    onClick={() => {
                      if (openCreateAppModal) {
                        openCreateAppModal()
                      }
                    }}
                  >
                    <span className='flex-shrink-0'>
                      <Icon name='plus-small' size={18} color='primary' />
                    </span>
                    <Text className='flex-grow truncate max-w-[180px] text-sm text-primary'>Add an App</Text>
                  </DropdownMenuItem>
                </>
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Only render the CreateAppPath component if the user has permission */}
      {hasPermission({ resource: identity.account, permissionKey: 'app:create' }) && (
        <CreateAppPath externalTrigger={handleExternalTrigger} hideUI />
      )}
    </>
  )
}
